// Staff roles as Flow shows them. Mirrors apps/api/src/services/auth.ts
// STAFF_ROLES and services/staff-access.ts (the server enforces; this only
// decides what to show). The offline gate qa-marketing-foundation checks the
// two lists agree.

export const STAFF_ROLE_OPTIONS: Array<{ value: string; label: string; hint: string }> = [
  { value: "admin", label: "Admin", hint: "Full access + user management" },
  { value: "client_rep", label: "Client rep", hint: "Sales + client work" },
  { value: "order_rep", label: "Order rep", hint: "Order entry + processing" },
  { value: "production_rep", label: "Production rep", hint: "Production + purchasing" },
  { value: "finance", label: "Finance", hint: "Billing, invoicing, payouts" },
  { value: "marketing", label: "Marketing", hint: "Hub ▸ Marketing + Flow Reports, read-only everywhere else" },
];

const OPERATIONS_ROLES = ["admin", "client_rep", "order_rep", "production_rep", "finance"];

/** Holds `marketing` and no operational role: sees Reports only in Flow. */
export function isMarketingOnly(roles: readonly string[] | null | undefined): boolean {
  return !!roles && roles.includes("marketing") && !roles.some((r) => OPERATIONS_ROLES.includes(r));
}

/** The roles people see and pick (Stephanie 2026-10-26: "order rep and sales
 *  rep … should just be 'sales'", "production_rep should just be
 *  production"). Each choice grants the server roles listed — Sales gives BOTH
 *  client_rep and order_rep, so a salesperson can do either side and shows in
 *  both rep pickers. The server's role names don't change. */
export const ROLE_CHOICES: Array<{ key: string; label: string; hint: string; grants: string[] }> = [
  { key: "admin", label: "Admin", hint: "Full access + user management", grants: ["admin"] },
  { key: "sales", label: "Sales", hint: "Accounts, quotes and orders (account rep + order rep)", grants: ["client_rep", "order_rep"] },
  { key: "production", label: "Production", hint: "Production + purchasing", grants: ["production_rep"] },
  { key: "finance", label: "Finance", hint: "Billing, invoicing, payouts", grants: ["finance"] },
  { key: "marketing", label: "Marketing", hint: "Hub ▸ Marketing + Flow Reports, read-only everywhere else", grants: ["marketing"] },
];

/** Pure: which choices a person has (Sales if they hold either rep role). */
export function roleChoicesOf(roles: readonly string[]): string[] {
  return ROLE_CHOICES.filter((c) => c.grants.some((g) => roles.includes(g))).map((c) => c.key);
}

/** Pure: the server roles for a set of choices (in a stable order). */
export function rolesFromChoices(keys: readonly string[]): string[] {
  return [...new Set(ROLE_CHOICES.filter((c) => keys.includes(c.key)).flatMap((c) => c.grants))];
}

/** Pure: "Production · Finance · Admin" — the names people see. */
export function roleWords(roles: readonly string[]): string {
  return ROLE_CHOICES.filter((c) => c.grants.some((g) => roles.includes(g))).map((c) => c.label).join(" · ");
}
